import React, { useCallback, useRef, useState } from "react";
import { X, Download, Share2, Copy, Check, TrendingUp, TrendingDown, Zap } from "lucide-react";
import { toPng, toBlob } from "html-to-image";
import { fmtLocal } from "../utils/time";

const money = (v) => (v == null ? "—" : `${v >= 0 ? "+" : ""}${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`);
const fmtDateTime = (s) => fmtLocal(s);
const fmtDur = (sec) => {
  if (sec == null) return "—";
  const d = Math.floor(sec / 86400), h = Math.floor((sec % 86400) / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60;
  if (d) return `${d}d ${h}h`;
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m ${s}s`;
  return `${s}s`;
};

const Stat = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "rgba(148,166,197,0.75)" }}>{label}</div>
    <div className="text-[15px] font-bold tabular-nums" style={{ color: "#fff" }}>{value}</div>
  </div>
);

const ShareCard = ({ detail, onClose }) => {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const win = detail.net_pnl >= 0;
  const buy = detail.direction === "BUY";
  const accent = win ? "#38bdf8" : "#f87171";
  // Reliable % = favourable price move relative to entry (0 commissions/swap on these deals).
  const pnlPct = (detail.entry_price && detail.exit_price)
    ? ((detail.exit_price - detail.entry_price) / detail.entry_price) * 100 * (buy ? 1 : -1)
    : null;
  const fileBase = `WealthSecret-${detail.symbol || "trade"}-${detail.result || ""}`.replace(/[^\w.-]+/g, "_");

  const download = useCallback(async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const url = await toPng(cardRef.current, { pixelRatio: 2.5, cacheBust: true });
      const a = document.createElement("a"); a.href = url; a.download = `${fileBase}.png`; a.click();
    } catch (e) { /* noop */ }
    setBusy(false);
  }, [fileBase]);

  const share = useCallback(async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const blob = await toBlob(cardRef.current, { pixelRatio: 2.5, cacheBust: true });
      const file = new File([blob], `${fileBase}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "My Wealth Secret trade" });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = `${fileBase}.png`; a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) { /* cancelled */ }
    setBusy(false);
  }, [fileBase]);

  const copy = useCallback(async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const blob = await toBlob(cardRef.current, { pixelRatio: 2.5, cacheBust: true });
      await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);
      setCopied(true); setTimeout(() => setCopied(false), 1800);
    } catch (e) { /* clipboard image unsupported */ }
    setBusy(false);
  }, []);

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" style={{ background: "rgba(3,7,18,0.82)", backdropFilter: "blur(6px)" }} data-testid="share-card-modal" onClick={onClose}>
      <div className="flex flex-col items-center gap-4" onClick={(e) => e.stopPropagation()}>
        {/* The exportable card */}
        <div
          ref={cardRef}
          data-testid="share-card"
          style={{
            width: 400, borderRadius: 24, overflow: "hidden", position: "relative",
            background: "radial-gradient(120% 90% at 80% -10%, rgba(56,189,248,0.14), rgba(0,0,0,0) 55%), linear-gradient(160deg, #0a1224 0%, #060a14 100%)",
            border: "1px solid rgba(80,130,220,0.22)", boxShadow: `0 30px 80px rgba(0,0,0,0.6), inset 0 0 0 1px rgba(255,255,255,0.02)`,
          }}
        >
          {/* Accent glow strip */}
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: `linear-gradient(90deg, ${accent}, transparent 70%)` }} />
          <div style={{ padding: 28 }}>
            {/* Brand */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 24 }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", background: "#0a1224", boxShadow: "0 6px 18px rgba(56,189,248,0.35)", border: "1px solid rgba(255,255,255,0.08)" }}>
                <img src="/wsj-logo.webp" alt="Wealth Secret" crossOrigin="anonymous" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </div>
              <div style={{ lineHeight: 1.1 }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: "#fff", letterSpacing: 0.3 }}>Wealth Secret</div>
                <div style={{ fontSize: 9, letterSpacing: 3, textTransform: "uppercase", color: "rgba(148,166,197,0.8)" }}>Journal</div>
              </div>
              <span style={{ marginLeft: "auto", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: win ? "#38bdf8" : "#f87171", background: "rgba(120,140,180,0.12)", padding: "4px 10px", borderRadius: 999, border: `1px solid ${accent}55` }}>{detail.result}</span>
            </div>

            {/* Symbol + side */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <span style={{ fontSize: 26, fontWeight: 800, color: "#fff" }}>{detail.symbol || "—"}</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 700, color: buy ? "#34d399" : "#f87171", background: buy ? "rgba(52,211,153,0.12)" : "rgba(248,113,113,0.12)", padding: "3px 9px", borderRadius: 8 }}>
                {buy ? "▲ BUY" : "▼ SELL"}
              </span>
            </div>
            <div style={{ fontSize: 11, color: "rgba(148,166,197,0.8)", marginBottom: 22 }}>{fmtDateTime(detail.exit_time || detail.entry_time)}</div>

            {/* Net P&L hero */}
            <div style={{ marginBottom: 22 }}>
              <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: "rgba(148,166,197,0.75)", marginBottom: 2 }}>Net Realized P&amp;L</div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 42, fontWeight: 800, letterSpacing: -1, color: accent, textShadow: `0 0 30px ${accent}55` }} data-testid="share-net-pnl">{money(detail.net_pnl)}</span>
                {pnlPct != null && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 13, fontWeight: 700, color: accent, background: "rgba(120,140,180,0.1)", padding: "4px 8px", borderRadius: 8, marginTop: 6 }}>
                    {win ? <TrendingUp style={{ width: 13, height: 13 }} /> : <TrendingDown style={{ width: 13, height: 13 }} />}
                    {pnlPct >= 0 ? "+" : ""}{pnlPct.toFixed(2)}%
                  </span>
                )}
              </div>
            </div>

            {/* Stats grid */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px 14px", padding: "16px", borderRadius: 14, background: "rgba(10,17,33,0.55)", border: "1px solid rgba(80,130,220,0.14)" }}>
              <Stat label="Entry" value={detail.entry_price ?? "—"} />
              <Stat label="Exit" value={detail.exit_price ?? "—"} />
              <Stat label="Volume" value={detail.volume != null ? `${detail.volume} lot` : "—"} />
              <Stat label="Duration" value={fmtDur(detail.duration_seconds)} />
            </div>

            {/* Footer */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 20 }}>
              <span style={{ fontSize: 10, color: "rgba(148,166,197,0.6)" }}>wealthsecretjournal.com</span>
              <span style={{ fontSize: 10, color: "rgba(148,166,197,0.6)" }}>Real MT5 result</span>
            </div>
          </div>
        </div>

        {/* Actions (not part of the exported image) */}
        <div className="flex items-center gap-2" data-testid="share-card-actions">
          <button onClick={download} disabled={busy} className="ws-btn-primary px-4 py-2.5 rounded-xl text-[13px] font-semibold inline-flex items-center gap-2 disabled:opacity-50" data-testid="share-download">
            <Download className="w-4 h-4" /> Download Image
          </button>
          <button onClick={share} disabled={busy} className="ws-btn-ghost px-4 py-2.5 rounded-xl text-[13px] font-semibold inline-flex items-center gap-2 disabled:opacity-50" data-testid="share-share">
            <Share2 className="w-4 h-4" /> Share
          </button>
          <button onClick={copy} disabled={busy} className="ws-btn-ghost px-4 py-2.5 rounded-xl text-[13px] font-semibold inline-flex items-center gap-2 disabled:opacity-50" data-testid="share-copy">
            {copied ? <><Check className="w-4 h-4 ws-up" /> Copied</> : <><Copy className="w-4 h-4" /> Copy</>}
          </button>
          <button onClick={onClose} className="ws-btn-ghost p-2.5 rounded-xl" data-testid="share-card-close"><X className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
};

export default ShareCard;
